import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import uploadIcon from "../../assets/icons/upload.svg";
import {
  Container,
  FormWrapper,
  SliderWrapper,
  RadioGroup,
  CheckboxGroup,
  ProofOfPaymentContainer,
  FileInputLabel,
  FileInput,
  PreviewImage,
} from "../../components/CampaignsComponent/CreateCampaignTab/CreateCampaignTabStyles";
import { Slider, FormControlLabel, Radio, Checkbox } from "@mui/material";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getCampaignById,
  updateCampaign,
  getAdvertiserInfo,
} from "../../redux/slices/campaignSlice";
import BackButton from "../../components/common/BackButton";
import { countries, objectives } from "../../shared/data/campaigns";

const EditCampaignTab = () => {
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  const { loading, advertiserInfo } = useSelector((state) => state.campaign);
  const [initialFormData, setInitialFormData] = useState(null); // To store original data for comparison

  useEffect(() => {
    dispatch(getAdvertiserInfo());
  }, [dispatch]);

  const advertiserOptions = advertiserInfo.map((advertiser) => ({
    value: advertiser.user.userReference,
    label: `${advertiser.user.firstName} ${advertiser.user.lastName}`,
  }));

  const [formData, setFormData] = useState({
    userReference: "",
    productId: "",
    campaignName: "",
    campaignUrl: "",
    campaignBudget: "",
    campaignObjective: "",
    ageRange: [25, 50],
    gender: "both",
    interests: {
      entertainment: false,
      lifestyle: false,
      gaming: false,
      music: false,
      others: false,
    },
    customInterests: [],  
    geo: "",
    startDate: "",
    endDate: "",
    campaignPhoto: null,
  });

  const [customInterestInput, setCustomInterestInput] = useState("");
  useEffect(() => {
    dispatch(getCampaignById(campaignId)).then((response) => {
      if (response.payload) {
        const fetchedData = response.payload.data;
        // const interestsArray = fetchedData?.interest || [];
        // console.log(fetchedData.user.userReference)

        // const updatedInterests = {
        //   sports: interestsArray.includes("sports"),
        //   travel: interestsArray.includes("travel"),
        //   fitness: interestsArray.includes("fitness"),
        //   tech: interestsArray.includes("tech"),
        //   health: interestsArray.includes("health"),
        //   others: interestsArray.includes("others"),
        // };
        const predefinedKeys = ["entertainment", "lifestyle", "gaming", "music"];
        // Extract custom interests: any interest not in predefinedKeys and not the literal "others"
        const customInterests = (fetchedData?.interest || []).filter(
          (interest) => !predefinedKeys.includes(interest) && interest !== "others"
        );
        // Build the interests object for the predefined keys.
        const updatedInterests = {
          entertainment: (fetchedData?.interest || []).includes("entertainment"),
          lifestyle: (fetchedData?.interest || []).includes("lifestyle"),
          gaming: (fetchedData?.interest || []).includes("gaming"),
          music: (fetchedData?.interest || []).includes("music"),
          // Mark "others" true if either "others" is in the array or there are custom interests
          others:
            (fetchedData?.interest || []).includes("others") ||
            customInterests.length > 0,
        };

        const existingFormData = {
          userReference: fetchedData.user.userReference,
          campaignName: fetchedData.campaignName || "",
          campaignUrl: fetchedData.campaignUrl || "",
          flow: fetchedData.flow || "",
          campaignBudget: fetchedData.campaignBudget || "",
          campaignObjective: fetchedData.campaignObjective || "",
          ageRange: fetchedData.ageRange
            ? fetchedData.ageRange.split("-").map(Number)
            : [25, 40],
          gender: fetchedData.gender || "both",
          interests: updatedInterests,
          //new
          customInterests: customInterests,
          geo: fetchedData.location || "",
          carrier: fetchedData.carrier || "",
          productId: fetchedData.productId || "",
          startDate: fetchedData.startDate || "",
          endDate: fetchedData.endDate || "",
          campaignPhoto:
            Array.isArray(fetchedData?.photoUrl) &&
            fetchedData.photoUrl.length > 0
              ? fetchedData.photoUrl[0]?.url
              : null,
        };

        setFormData(existingFormData);
        setInitialFormData(existingFormData); // Save initial data for comparison
      }
    });
  }, [dispatch, campaignId]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAgeChange = (event, newValue) => {
    setFormData({
      ...formData,
      ageRange: newValue,
    });
  };

  const handleGenderChange = (event) => {
    setFormData({
      ...formData,
      gender: event.target.value,
    });
  };

  // const handleInterestChange = (event) => {
  //   setFormData({
  //     ...formData,
  //     interests: {
  //       ...formData.interests,
  //       [event.target.name]: event.target.checked,
  //     },
  //   });
  // };

  const handleInterestChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      interests: {
        ...prevState.interests,
        [name]: checked,
      },
      // Optional: clear custom interests if "others" is unchecked.
      customInterests:
        name === "others" && !checked ? [] : prevState.customInterests,
    }));
  };

  const handleSelectChange = (selectedOption, name) => {
    setFormData({
      ...formData,
      [name]: selectedOption.value,
    });
  };

  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      campaignPhoto: e.target.files[0],
    });
  };

  const handleCustomInterestKeyDown = (event) => {
    if (event.key === "Enter" && customInterestInput.trim()) {
      event.preventDefault();
      if (!formData.customInterests.includes(customInterestInput.trim())) {
        setFormData((prevState) => ({
          ...prevState,
          customInterests: [
            ...prevState.customInterests,
            customInterestInput.trim(),
          ],
        }));
      }
      setCustomInterestInput("");
    }
  };

  const removeCustomInterest = (interest) => {
    setFormData((prevState) => ({
      ...prevState,
      customInterests: prevState.customInterests.filter((i) => i !== interest),
    }));
  };

  const trimFormValues = (values) => {
    const trimmedValues = {};
    Object.keys(values).forEach((key) => {
      if (typeof values[key] === "string") {
        trimmedValues[key] = values[key].trim(); // Trim strings
      } else if (typeof values[key] === "object" && values[key] !== null) {
        // Recursively trim nested objects if any
        trimmedValues[key] = trimFormValues(values[key]);
      } else {
        trimmedValues[key] = values[key]; // Keep other types as they are
      }
    });
    return trimmedValues;
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedFormData = trimFormValues(formData);
    const trimmedInitialData = trimFormValues(initialFormData);

    // Compare the trimmed form data
    if (
      JSON.stringify(trimmedFormData) === JSON.stringify(trimmedInitialData)
    ) {
      Swal.fire({
        title: "No Changes Made",
        text: "You have not made any changes to the campaign.",
        icon: "info",
      });
      return;
    }

    const form = new FormData();
    form.append("userReference", formData.userReference);
    form.append("campaignName", formData.campaignName);
    form.append("campaignUrl", formData.campaignUrl);
    form.append("flow", formData.flow);
    form.append("campaignObjective", formData.campaignObjective);
    form.append("campaignBudget", formData.campaignBudget);
    form.append("ageRange", `${formData.ageRange[0]}-${formData.ageRange[1]}`);
    form.append("gender", formData.gender);
    form.append("location", formData.geo);
    form.append("carrier", formData.carrier);
    form.append("productId", formData.productId.trim());
    const selectedInterests = Object.keys(formData.interests).filter(
      (interest) => formData.interests[interest]
    );

    // Append each selected interest individually
    selectedInterests.forEach((interest) => {
      form.append("interest[]", interest);
    });

    if (formData.customInterests.length > 0) {
      formData.customInterests.forEach((interest) => {
        form.append("interest[]", interest);
      });
    }

    if (formData.campaignPhoto) {
      form.append("files", formData.campaignPhoto);
    }
    //console.log(formData)
    //return
    dispatch(updateCampaign({ campaignId, form }));
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      marginTop: "10px",
      border: state.isFocused ? "solid 1px #03a9f4" : "solid 1px #e0e2e6",
      borderRadius: "10px",
      padding: "0px 14px",
      transition: "all 0.3s ease",
      height: "43px",
      boxShadow: state.isFocused ? "0 0 0 0px #03a9f4" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#03a9f4" : "",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#03a9f4"
        : state.isFocused
        ? "#e0f2f1"
        : provided.backgroundColor,
      color: state.isSelected ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#b2dfdb",
        color: "#000",
      },
    }),
  };

  return (
    <Container>
    <BackButton></BackButton>
      <div className="header">
        <div className="texts">
          <h3>Update Campaign</h3>
          <p>View your team's trades and transactions.</p>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <FormWrapper className="full-width">
          <label htmlFor="advertiser">Choose advertiser</label>
          <Select
            options={advertiserOptions}
            styles={customStyles}
            placeholder="Select advertiser"
            value={
              advertiserOptions.find(
                (option) => option.value === formData.userReference
              ) || null
            }
            onChange={(option) => handleSelectChange(option, "userReference")}
            required
          />
        </FormWrapper>

        <FormWrapper className="grid-row-3">
          <div className="field-group">
            <label htmlFor="campaignName">Campaign Name</label>
            <input
              type="text"
              id="campaignName"
              name="campaignName"
              placeholder="Campaign Name"
              value={formData.campaignName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="campaignUrl">Campaign Url</label>
            <input
              type="text"
              id="campaignUrl"
              name="campaignUrl"
              placeholder="Campaign Url"
              value={formData.campaignUrl}
              onChange={handleChange}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="flow">Flow</label>
            <input
              type="text"
              id="flow"
              name="flow"
              placeholder="Flow"
              value={formData.flow}
              onChange={handleChange}
              required
            />
          </div>
        </FormWrapper>

        <FormWrapper className="grid-row-3">
          <div className="field-group">
            <label htmlFor="campaignBudget">Campaign Budget ($)</label>
            <input
              type="number"
              id="campaignBudget"
              name="campaignBudget"
              placeholder="Enter your campaign budget in dollars"
              value={formData.campaignBudget}
              onChange={handleChange}
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="campaignObjective">Campaign objective</label>
            <Select
              options={objectives}
              styles={customStyles}
              placeholder="Select Objective"
              value={objectives.find(
                (obj) => obj.value === formData.campaignObjective
              )}
              onChange={(option) =>
                handleSelectChange(option, "campaignObjective")
              }
              required
            />
          </div>
          <div className="field-group">
            <label htmlFor="geo">Geo</label>
            <Select
              options={countries}
              styles={customStyles}
              placeholder="Select Location"
              value={countries.find((country) => country.value === formData.geo)}
              onChange={(option) => handleSelectChange(option, "geo")}
              required
            />
          </div>
        </FormWrapper>

        <FormWrapper className="grid-row">
          <div className="field-group">
            <label htmlFor="ageRange">Age range</label>
            <SliderWrapper>
              <Slider
                sx={{
                  color: "#03a9f4",
                  height: "6px",
                  "& .MuiSlider-thumb": {
                    backgroundColor: "#FFFFFF",
                    height: "18px",
                    width: "18px",
                    border: "2px solid #03a9f4",
                    boxShadow: "0 2px 4px rgba(3, 169, 244, 0.3)",
                    "&:hover": {
                      boxShadow: "0 2px 6px rgba(3, 169, 244, 0.4)",
                    },
                    "&:focus": {
                      boxShadow: "0 2px 6px rgba(3, 169, 244, 0.4)",
                    },
                  },
                  "& .MuiSlider-track": {
                    backgroundColor: "#03a9f4",
                    height: "6px",
                    border: "none",
                  },
                  "& .MuiSlider-rail": {
                    height: "6px",
                    backgroundColor: "#e3f2fd",
                    opacity: 1,
                  },
                }}
                id="ageRange"
                name="ageRange"
                min={10}
                max={100}
                step={1}
                value={formData.ageRange}
                onChange={handleAgeChange}
                valueLabelDisplay="auto"
              />
            </SliderWrapper>
          </div>
          <div className="field-group">
            <div>
              <label>Preferred gender</label>
              <p>Choose the gender you will want your ads to be targeted at</p>
            </div>
            <RadioGroup>
              <FormControlLabel
                control={
                  <Radio
                    sx={{
                      padding: "0px 10px",
                      color: "#D9DCE1",
                      "&.Mui-checked": {
                        color: "#03a9f4",
                      },
                    }}
                  />
                }
                label="Male"
                value="male"
                checked={formData.gender === "male"}
                onChange={handleGenderChange}
              />
              <FormControlLabel
                control={
                  <Radio
                    sx={{
                      padding: "0px 10px",
                      color: "#D9DCE1",
                      "&.Mui-checked": {
                        color: "#03a9f4",
                      },
                    }}
                  />
                }
                label="Female"
                value="female"
                checked={formData.gender === "female"}
                onChange={handleGenderChange}
              />
              <FormControlLabel
                control={
                  <Radio
                    sx={{
                      padding: "0px 10px",
                      color: "#D9DCE1",
                      "&.Mui-checked": {
                        color: "#03a9f4",
                      },
                    }}
                  />
                }
                label="Both"
                value="both"
                checked={formData.gender === "both"}
                onChange={handleGenderChange}
              />
            </RadioGroup>
          </div>
        </FormWrapper>

        <FormWrapper className="full-width">
          <div>
            <label>Interest</label>
            <p>Choose the kind of user's interest you will like to target</p>
          </div>
          <CheckboxGroup>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#03a9f4",
                    },
                  }}
                  checked={formData.interests.entertainment}
                  onChange={handleInterestChange}
                  name="entertainment"
                />
              }
              label="Entertainment"
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#03a9f4",
                    },
                  }}
                  checked={formData.interests.lifestyle}
                  onChange={handleInterestChange}
                  name="lifestyle"
                />
              }
              label="Lifestyle"
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#03a9f4",
                    },
                  }}
                  checked={formData.interests.gaming}
                  onChange={handleInterestChange}
                  name="gaming"
                />
              }
              label="Gaming"
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#03a9f4",
                    },
                  }}
                  checked={formData.interests.music}
                  onChange={handleInterestChange}
                  name="music"
                />
              }
              label="Music"
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    padding: "0px 10px",
                    color: "#D9DCE1",
                    "&.Mui-checked": {
                      color: "#03a9f4",
                    },
                  }}
                  checked={formData.interests.others}
                  onChange={handleInterestChange}
                  name="others"
                />
              }
              label="Others"
            />
          </CheckboxGroup>
          <p></p>
          {formData.interests.others && (
            <div style={{ marginTop: "-25px" }}>
              <input
                type="text"
                placeholder="Enter new interest and press Enter"
                value={customInterestInput}
                onChange={(e) => setCustomInterestInput(e.target.value)}
                onKeyDown={handleCustomInterestKeyDown}
                style={{
                  padding: "8px",
                  width: "100%",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                }}
              />
              <div style={{ marginTop: "10px" }}>
                {formData.customInterests.map((interest, index) => (
                  <span
                    key={index}
                    style={{
                      display: "inline-block",
                      background: "#e0f2f1",
                      padding: "5px 10px",
                      borderRadius: "15px",
                      marginRight: "5px",
                      marginBottom: "5px",
                    }}
                  >
                    {interest}
                    <button
                      onClick={() => removeCustomInterest(interest)}
                      style={{
                        marginLeft: "5px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                      type="button"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </FormWrapper>

        <FormWrapper className="full-width">
          <label htmlFor="carrier">Carrier</label>
          <input
            type="text"
            id="carrier"
            name="carrier"
            placeholder="Enter the carrier"
            value={formData.carrier}
            onChange={handleChange}
            required
          />
        </FormWrapper>

        <FormWrapper className="full-width">
          <label htmlFor="productId">Service ID</label>
          <input
            type="text"
            id="productId"
            name="productId"
            placeholder="Service ID (sent to POST publishers as {serviceID})"
            value={formData.productId}
            onChange={handleChange}
          />
        </FormWrapper>

        <FormWrapper>
          <label htmlFor="campaignPhoto">Campaign Photo</label>

          <ProofOfPaymentContainer>
            <FileInputLabel htmlFor="campaignPhoto">
              {formData.campaignPhoto ? (
                <PreviewImage
                  src={
                    typeof formData.campaignPhoto === "string"
                      ? formData.campaignPhoto
                      : URL.createObjectURL(formData.campaignPhoto)
                  }
                  alt="Campaign Photo"
                />
              ) : (
                <div className="text">
                  <img src={uploadIcon} alt="" />
                  <p>Click to upload</p>
                  {/* <small>PNG, JPG</small> */}
                </div>
              )}
              <FileInput
                type="file"
                id="campaignPhoto"
                name="campaignPhoto"
                accept=".svg,.png,.jpg"
                onChange={handleFileChange}
              />
            </FileInputLabel>
          </ProofOfPaymentContainer>
        </FormWrapper>

        {/* <FormWrapper>
          <label>Date</label>
          <div className="date">
            <div>
              <label htmlFor="startDate">Start date</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label htmlFor="endDate">End date</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </FormWrapper> */}

        <div className="submit">
          <div></div>
          <div className="submit-buttons">
            <button className="add-campaign" type="submit" disabled={loading}>
              {loading ? "Updating Campaign..." : "Update Campaign"}
            </button>
          </div>
        </div>
      </form>
    </Container>
  );
};

export default EditCampaignTab;
